import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truck, Buildings, User, CheckCircle, ArrowLeft, ArrowRight, Package, Anchor } from '@phosphor-icons/react'
import { selfSignup, SelfSignupData } from '../services/auth'
import { getCountryConfigs, CountryConfig } from '../services/companies'
import { COUNTRIES, formatPhoneNumber } from '../data/countries'

type CompanyType = 'shipper' | 'freight-forwarder'

/**
 * Progressively format a CNPJ as the user types: XX.XXX.XXX/XXXX-XX
 */
function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14)
  let result = digits
  if (digits.length > 2) result = digits.slice(0, 2) + '.' + digits.slice(2)
  if (digits.length > 5) result = result.slice(0, 6) + '.' + digits.slice(5)
  if (digits.length > 8) result = result.slice(0, 10) + '/' + digits.slice(8)
  if (digits.length > 12) result = result.slice(0, 15) + '-' + digits.slice(12)
  return result
}

/**
 * Progressively format a phone number with dial code prefix.
 * Uses the mask from COUNTRIES data based on the selected country code.
 */
function maskPhone(value: string, countryCode: string): string {
  const country = COUNTRIES.find(c => c.code === countryCode)
  const dialCode = country?.dialCode || '+55'
  const mask = country?.mask

  // Strip everything except digits and leading +
  let raw = value.replace(/[^\d+]/g, '')

  // If user cleared the field, return empty
  if (!raw || raw === '+' || raw === dialCode) return ''

  // Ensure dial code prefix
  if (!raw.startsWith('+')) {
    // If they typed digits without +, assume local number
    raw = dialCode + raw
  } else if (!raw.startsWith(dialCode)) {
    // If they changed country but kept old dial code, replace it
    const digitsOnly = raw.replace(/\D/g, '')
    raw = dialCode + digitsOnly
  }

  // Extract the local part (after dial code)
  const localDigits = raw.slice(dialCode.length).replace(/\D/g, '')

  if (!localDigits) return ''

  const formatted = mask ? formatPhoneNumber(localDigits, mask) : localDigits
  return `${dialCode} ${formatted}`
}

interface FormData extends SelfSignupData {
  confirmPassword: string
}

export default function SignupPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [countries, setCountries] = useState<CountryConfig[]>([])
  const [error, setError] = useState('')

  const [formData, setFormData] = useState<FormData>({
    // User data
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    // Company data
    companyType: 'shipper',
    companyName: '',
    tradingName: '',
    countryCode: 'BR',
    primaryIdentifier: '',
    primaryIdentifierType: 'CNPJ'
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load country configs
  useEffect(() => {
    async function loadCountries() {
      try {
        const data = await getCountryConfigs()
        setCountries(data)
        const defaultCountry = data.find(c => c.countryCode === 'BR')
        if (defaultCountry) {
          setFormData(prev => ({
            ...prev,
            primaryIdentifierType: defaultCountry.primaryIdentifierType
          }))
        }
      } catch (err) {
        console.error('Error loading countries:', err)
      } finally {
        setLoadingCountries(false)
      }
    }
    loadCountries()
  }, [])

  // Update identifier type when country changes
  useEffect(() => {
    const country = countries.find(c => c.countryCode === formData.countryCode)
    if (country) {
      setFormData(prev => ({
        ...prev,
        primaryIdentifierType: country.primaryIdentifierType
      }))
    }
  }, [formData.countryCode, countries])

  const selectedCountry = countries.find(c => c.countryCode === formData.countryCode)

  function validateStep(step: number): boolean {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      // Company type is always selected
      return true
    }

    if (step === 2) {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Nome da empresa é obrigatório'
      }
      if (!formData.primaryIdentifier.trim()) {
        newErrors.primaryIdentifier = `${selectedCountry?.identifierLabel || 'Identificador'} é obrigatório`
      }
    }

    if (step === 3) {
      if (!formData.name.trim()) {
        newErrors.name = 'Nome é obrigatório'
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email é obrigatório'
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email inválido'
      }
      if (!formData.password) {
        newErrors.password = 'Senha é obrigatória'
      } else if (formData.password.length < 8) {
        newErrors.password = 'Senha deve ter no mínimo 8 caracteres'
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter ao menos uma letra maiúscula'
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter ao menos um número'
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'As senhas não coincidem'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleNext() {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4))
    }
  }

  function handleBack() {
    setCurrentStep(prev => Math.max(prev - 1, 1))
    setError('')
  }

  async function handleSubmit() {
    if (!validateStep(3)) return

    try {
      setLoading(true)
      setError('')

      const { confirmPassword, ...signupData } = formData
      const response = await selfSignup(signupData)

      // Success! Redirect to dashboard
      if (response.redirectTo) {
        navigate(response.redirectTo)
      } else {
        navigate('/shipper/dashboard')
      }
    } catch (err: any) {
      console.error('Signup error:', err)
      setError(err.response?.data?.error || 'Erro ao criar conta. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  if (loadingCountries) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Carregando...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: 'rgb(37, 48, 55)' }}>
      {/* Left Side - Branding (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
              <Truck size={32} weight="bold" className="text-white" />
            </div>
            <h1 className="text-3xl font-bold">Eximtech</h1>
          </div>

          <div className="mt-12">
            <h2 className="text-4xl font-bold mb-4">Comece gratuitamente</h2>
            <p className="text-lg text-slate-300">
              Crie sua conta e gerencie seus fretes internacionais com facilidade
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <CheckCircle size={32} weight="bold" className="mb-3 text-green-500" />
            <p className="text-sm font-medium">Cotações Rápidas</p>
          </div>
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <CheckCircle size={32} weight="bold" className="mb-3 text-green-500" />
            <p className="text-sm font-medium">Multi-País</p>
          </div>
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <CheckCircle size={32} weight="bold" className="mb-3 text-green-500" />
            <p className="text-sm font-medium">Seguro</p>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden w-full max-w-md mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="bg-green-500 rounded-2xl p-3 shadow-lg">
              <Truck size={28} weight="bold" className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">Eximtech</h1>
          </div>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-2xl">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 lg:p-8 shadow-2xl border border-slate-700/40">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300">
                  Passo {currentStep} de 4
                </span>
                <span className="text-sm text-slate-400">
                  {currentStep === 1 && 'Tipo de Empresa'}
                  {currentStep === 2 && 'Dados da Empresa'}
                  {currentStep === 3 && 'Seus Dados'}
                  {currentStep === 4 && 'Revisão'}
                </span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: 'rgb(5, 150, 105)',
                    width: `${(currentStep / 4) * 100}%`
                  }}
                />
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Company Type */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Qual o tipo da sua empresa?</h3>
                  <p className="text-slate-400">Selecione a opção que melhor descreve sua atividade</p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="companyType"
                      value="shipper"
                      checked={formData.companyType === 'shipper'}
                      onChange={(e) => setFormData({ ...formData, companyType: e.target.value as CompanyType })}
                      className="sr-only"
                    />
                    <div className={`p-6 border-2 rounded-xl transition-all ${
                      formData.companyType === 'shipper'
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <Package size={24} className="text-blue-400" weight="duotone" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-lg text-white mb-1">Sou Embarcador</div>
                          <div className="text-sm text-slate-300">
                            Preciso enviar cargas internacionalmente e busco cotações de frete
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>

                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="companyType"
                      value="freight-forwarder"
                      checked={formData.companyType === 'freight-forwarder'}
                      onChange={(e) => setFormData({ ...formData, companyType: e.target.value as CompanyType })}
                      className="sr-only"
                    />
                    <div className={`p-6 border-2 rounded-xl transition-all ${
                      formData.companyType === 'freight-forwarder'
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <Anchor size={24} className="text-emerald-400" weight="duotone" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-lg text-white mb-1">Sou Agente de Carga</div>
                          <div className="text-sm text-slate-300">
                            Forneço serviços de frete internacional e quero receber solicitações de cotação
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Company Data */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Dados da sua empresa</h3>
                  <p className="text-slate-400">Informações da empresa e país de registro</p>
                </div>

                <div className="space-y-4">
                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      País <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {countries.map(country => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.countryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Razão Social <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                        errors.companyName ? 'border-red-500' : 'border-slate-600'
                      } focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Nome legal da empresa"
                    />
                    {errors.companyName && (
                      <p className="mt-1 text-sm text-red-400">{errors.companyName}</p>
                    )}
                  </div>

                  {/* Trading Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nome Fantasia
                    </label>
                    <input
                      type="text"
                      value={formData.tradingName}
                      onChange={(e) => setFormData({ ...formData, tradingName: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Nome comercial (opcional)"
                    />
                  </div>

                  {/* Primary Identifier */}
                  {selectedCountry && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {selectedCountry.identifierLabel} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.primaryIdentifier}
                        onChange={(e) => {
                          const val = e.target.value
                          const formatted = formData.countryCode === 'BR' ? maskCNPJ(val) : val
                          setFormData({ ...formData, primaryIdentifier: formatted })
                        }}
                        className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                          errors.primaryIdentifier ? 'border-red-500' : 'border-slate-600'
                        } focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder={selectedCountry.identifierExample}
                        maxLength={formData.countryCode === 'BR' ? 18 : undefined}
                      />
                      {errors.primaryIdentifier && (
                        <p className="mt-1 text-sm text-red-400">{errors.primaryIdentifier}</p>
                      )}
                      <p className="mt-1 text-xs text-slate-400">
                        Formato: {selectedCountry.identifierExample}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: User Data */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Seus dados</h3>
                  <p className="text-slate-400">Crie sua conta de acesso</p>
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nome Completo <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                        errors.name ? 'border-red-500' : 'border-slate-600'
                      } focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Seu nome completo"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      E-mail <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                        errors.email ? 'border-red-500' : 'border-slate-600'
                      } focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="seu@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const formatted = maskPhone(e.target.value, formData.countryCode)
                        setFormData({ ...formData, phone: formatted })
                      }}
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder={(() => {
                        const c = COUNTRIES.find(c => c.code === formData.countryCode)
                        return c ? `${c.dialCode} ${c.placeholder || ''}` : '+55 (11) 98765-4321'
                      })()}
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Senha <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                        errors.password ? 'border-red-500' : 'border-slate-600'
                      } focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Mínimo 8 caracteres"
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                    )}
                    <p className="mt-1 text-xs text-slate-400">
                      Mínimo 8 caracteres, 1 maiúscula e 1 número
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Confirmar Senha <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg bg-slate-700 text-white border ${
                        errors.confirmPassword ? 'border-red-500' : 'border-slate-600'
                      } focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Digite a senha novamente"
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Revisar e confirmar</h3>
                  <p className="text-slate-400">Verifique seus dados antes de continuar</p>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Buildings size={20} className="text-green-500" weight="duotone" />
                      <h4 className="font-semibold text-white">Empresa</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Tipo:</span>
                        <span className="text-white font-medium">
                          {formData.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Nome:</span>
                        <span className="text-white font-medium">{formData.companyName}</span>
                      </div>
                      {formData.tradingName && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Nome Fantasia:</span>
                          <span className="text-white font-medium">{formData.tradingName}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-400">País:</span>
                        <span className="text-white font-medium">
                          {selectedCountry?.countryName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">{selectedCountry?.identifierLabel}:</span>
                        <span className="text-white font-medium">{formData.primaryIdentifier}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <User size={20} className="text-green-500" weight="duotone" />
                      <h4 className="font-semibold text-white">Usuário</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Nome:</span>
                        <span className="text-white font-medium">{formData.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">E-mail:</span>
                        <span className="text-white font-medium">{formData.email}</span>
                      </div>
                      {formData.phone && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Telefone:</span>
                          <span className="text-white font-medium">{formData.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1 || loading}
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={18} />
                Voltar
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition-all hover:shadow-lg active:scale-95 disabled:opacity-50"
                  style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                >
                  Continuar
                  <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition-all hover:shadow-lg active:scale-95 disabled:opacity-50"
                  style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                >
                  {loading ? 'Criando conta...' : 'Começar agora!'}
                  {!loading && <CheckCircle size={18} weight="bold" />}
                </button>
              )}
            </div>

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-400">
                Já tem uma conta?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="font-medium hover:underline"
                  style={{ color: 'rgb(5, 150, 105)' }}
                >
                  Fazer login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
